import React, { PropsWithChildren, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import Auth from '@aws-amplify/auth';

import CircularProgress from '@material-ui/core/CircularProgress';
import { ProfileContext, Profile } from 'app/contexts/profile-context';
import { setLocalStorage } from 'services/helpers/local-storage';
import { signOut } from 'services/helpers/signOut';
import { pick, isEqual } from 'lodash';
import { Box } from '@material-ui/core';

import { decodeToken } from 'services/helpers/jwt';
import {
  DEFAULT_DOMESTIC_COUNTRY_CODE,
  DEFAULT_DOMESTIC_CURRENCY,
  DEFAULT_UNIT_MEASUREMENT,
} from 'services/helpers/constants';
import { getBrowserLocales } from 'services/helpers/getBrowserLocales';
import { SUPPORTED_LANGUAGE } from 'services/helpers/constants';

const getJWTToken = async () => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (error) {
    console.error('Error getting JWT token:', error);
    return null;
  }
};

const getCurrentUser = async (params): Promise<{ data: Profile } | undefined> => {
  const now = Date.now();
  // Read cache from sessionStorage
  const cachedAttributesStr = sessionStorage.getItem('cachedUserAttributes');
  const cachedFetchTimeStr = sessionStorage.getItem('cachedUserFetchTime');
  const cachedAttributes = cachedAttributesStr ? JSON.parse(cachedAttributesStr) : null;
  const cachedFetchTime = cachedFetchTimeStr ? parseInt(cachedFetchTimeStr, 10) : 0;

  // If bypassCache is not true and last fetch was within 60 seconds, return cached user
  if (!params.bypassCache && cachedAttributes && now - cachedFetchTime < 60000) {
    return {
      data: cachedAttributes,
    };
  }

  try {
    const { attributes } = await Auth.currentAuthenticatedUser({
      bypassCache: params.bypassCache === undefined ? true : params.bypassCache,
    });

    const jwtToken = await getJWTToken();

    if (!attributes || !jwtToken) {
      throw new Error('The user is not authenticated');
    }

    const decodedToken = await decodeToken(jwtToken);

    setLocalStorage(
      'domesticCountryCode',
      decodedToken['custom:c_code'] || DEFAULT_DOMESTIC_COUNTRY_CODE
    );

    setLocalStorage('domesticCurrency', decodedToken['custom:curr'] || DEFAULT_DOMESTIC_CURRENCY);

    setLocalStorage('unitMeasurement', decodedToken['custom:units'] || DEFAULT_UNIT_MEASUREMENT);

    const browserLocales = getBrowserLocales({ languageCodeOnly: true });

    const domesticLanguages = browserLocales?.filter((locale) =>
      SUPPORTED_LANGUAGE.includes(locale)
    );

    const userData = {
      ...decodedToken,
      ...pick(attributes, ['given_name', 'family_name', 'email', 'phone_number', 'sub']),
      domesticCountryCode: decodedToken['custom:c_code'],
      domesticCurrency: decodedToken['custom:curr'],
      domesticLanguages,
      unitMeasurement: decodedToken['custom:units'],
      stripeVerificationStatus: decodedToken['custom:kyc_status'],
      id: params.id,
    };

    // Update cache in sessionStorage
    sessionStorage.setItem('cachedUserAttributes', JSON.stringify(userData));
    sessionStorage.setItem('cachedUserFetchTime', now.toString());

    return {
      data: userData,
    };
  } catch (error) {
    if (
      error === 'The user is not authenticated' ||
      (error instanceof Error && error.message === 'The user is not authenticated')
    ) {
      await signOut();
    }
  }
};

export const ProfileProvider = ({ children }: PropsWithChildren<{}>) => {
  const [profile, setProfile] = useState<Profile | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const tokenRefreshPromiseRef = useRef<Promise<void> | null>(null);
  const pendingTokenRefreshRef = useRef(false);

  const getProfile = useCallback(
    async (params?: { bypassCache?: boolean; showLoading?: boolean }) => {
      const shouldShowLoading = params?.showLoading !== false; // Default to true

      if (shouldShowLoading) {
        setLoading(true);
      }

      try {
        const result = await getCurrentUser({
          id: 'profile',
          bypassCache: params?.bypassCache,
        });

        // Only update if the result is different from current profile
        if (!isEqual(result?.data, profile)) {
          // set user sub to local storage to be used by countryCode helper
          setLocalStorage('user-sub', result?.data?.sub);

          setProfile(result?.data);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        if (shouldShowLoading) {
          setLoading(false);
        }
      }
    },
    [profile]
  );

  const domesticCountryAttributes = useMemo(() => {
    return {
      domesticCountryCode: profile?.domesticCountryCode,
      domesticCurrency: profile?.domesticCurrency,
      unitMeasurement: profile?.unitMeasurement,
    };
  }, [profile]);

  const refreshProfileAfterTokenRefresh = useCallback(() => {
    if (tokenRefreshPromiseRef.current) {
      pendingTokenRefreshRef.current = true;
      return tokenRefreshPromiseRef.current;
    }

    pendingTokenRefreshRef.current = false;

    const refreshPromise = getProfile({
      bypassCache: true,
      showLoading: false,
    }).finally(() => {
      tokenRefreshPromiseRef.current = null;

      if (pendingTokenRefreshRef.current) {
        pendingTokenRefreshRef.current = false;
        void refreshProfileAfterTokenRefresh();
      }
    });

    tokenRefreshPromiseRef.current = refreshPromise;

    return refreshPromise;
  }, [getProfile]);

  useEffect(() => {
    if (!profile) {
      getProfile();
    }
  }, [getProfile, profile]);

  // Listen for token refresh events and update profile
  useEffect(() => {
    const handleTokenRefresh = () => {
      console.log('Token refreshed, updating profile...');
      void refreshProfileAfterTokenRefresh();
    };

    window.addEventListener('tokenRefreshed', handleTokenRefresh);

    return () => {
      window.removeEventListener('tokenRefreshed', handleTokenRefresh);
    };
  }, [refreshProfileAfterTokenRefresh]);

  if (loading || !profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ProfileContext.Provider
      value={{
        profile,
        getProfile,
        loading,
        ...domesticCountryAttributes,
      }}>
      {children}
    </ProfileContext.Provider>
  );
};
